import React, { useCallback } from 'react';
import { Image, Stack, TooltipHost } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import clsx from 'clsx';
import { useAppTheme } from 'common';
import { Text } from 'common/components/Text';
import TelemetryProvider from 'common/utilities/TelemetryProvider';

import { IArtifactViewProps } from './ArtifactPile.types';
import { useArtifactViewStyles } from './ArtifactView.styles';

export const ArtifactView = (props: IArtifactViewProps) => {
  const {
    className,
    baseSize = 28,
    styles,
    IconComponent,
    text,
    imageUrl,
    title,
    onClick,
    hasError = false,
    telemetryPrefix,
    onFocus,
    onZoomIn,
    onZoomOut,
    showTooltip = true,
    zoomOnFocus = true,
  } = props;

  // size is 4px smaller when hasError === true because an extra border of 2px (both sides = 4px) will be added
  const size = hasError ? baseSize - 4 : baseSize;
  const classNames = useArtifactViewStyles(hasError, size, zoomOnFocus);
  const theme = useAppTheme();
  const tooltipId = useId('artifact-view-tooltip-');

  const handleOnClick = useCallback(
    (e: React.UIEvent) => {
      e?.stopPropagation();
      e?.preventDefault();
      TelemetryProvider.Instance.logUserActivity(`${telemetryPrefix}-click`, 'ClickActivity');
      onClick();
    },
    [onClick, telemetryPrefix]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const key = e?.nativeEvent?.code;

      if (['Enter', 'Space'].includes(key)) {
        handleOnClick(e);
      }
    },
    [handleOnClick]
  );

  const handleOnFocus = useCallback(() => {
    onFocus?.();
    onZoomIn?.();
  }, [onFocus, onZoomIn]);

  const renderImageContainer = useCallback(() => {
    if (imageUrl) {
      return <Image shouldStartVisible src={imageUrl} width={size} height={size} alt="" className={classNames.image} />;
    }
    return (
      <Stack horizontalAlign="center" verticalAlign="center" verticalFill className={classNames.textOrIcon}>
        {text && (
          <Text nowrap block variant="Caption2Strong" showTooltipOnOverflow>
            {text}
          </Text>
        )}
        {IconComponent && <IconComponent primaryFill={theme.appColors?.lightHeadlineText} />}
      </Stack>
    );
  }, [imageUrl, classNames.textOrIcon, classNames.image, text, IconComponent, theme.appColors?.lightHeadlineText, size]);

  const renderContent = useCallback(
    () => (
      <Stack
        className={clsx(classNames.root, className)}
        styles={styles}
        verticalFill={true}
        horizontalAlign="center"
        verticalAlign="center"
        aria-label={title}
        role="button"
        tabIndex={0}
        onClick={handleOnClick}
        onKeyDown={handleKeyDown}
        onFocus={handleOnFocus}
        onBlur={onZoomOut}
        onMouseEnter={onZoomIn}
        onMouseLeave={onZoomOut}
      >
        <Stack
          className={clsx(classNames.contentContainer, hasError && classNames.errorContentContainer)}
          verticalFill={true}
          horizontalAlign="center"
          verticalAlign="center"
        >
          {renderImageContainer()}
        </Stack>
      </Stack>
    ),
    [
      classNames.root,
      classNames.contentContainer,
      classNames.errorContentContainer,
      className,
      styles,
      title,
      handleOnClick,
      handleKeyDown,
      handleOnFocus,
      onZoomOut,
      onZoomIn,
      hasError,
      renderImageContainer,
    ]
  );

  return showTooltip ? (
    <TooltipHost content={title} id={tooltipId} styles={{ root: { width: '100%', height: '100%' } }}>
      {renderContent()}
    </TooltipHost>
  ) : (
    renderContent()
  );
};
